import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useStyles from 'app/cluster-styles/useStyles';
import { CLUSTER_STATES } from 'app/main/util/clusterUtils';
import { IMAGE_TYPE } from '../../util/enums';
import { ipValid } from '../../util/InputValidate';
import * as StackActions from 'app/main/stacks/store/actions/stacks.actions.js';
import * as ActionsDialog from 'app/store/actions';
import * as Actions from '../store/actions';
import { getClusterSettings, getOrgClusterSettings } from 'app/main/settings/store/actions';

import useMarketplace from './useMarketplace';
import useClusterOwner from './useClusterOwner';
import useProviderData from './useProviderData';
import { useInstanceAvailability } from './useInstanceAvailability';
import useQuotas from './useQuotas';

/**
 * Shared form lifecycle hook for all cluster provider forms.
 * Encapsulates: state, Redux selectors, hooks composition, init effect, handlers.
 *
 * @param {string} provider - 'ibm'|'azure'|'gcp'|'oracle'|'aws'
 * @param {object} props - component props (type, oldForm, marketId)
 * @param {object} formHelpers - { toApiShape, initNewForm, initEditForm }
 * @param {function} [extraValidation] - (form) => true | false | string for provider-specific checks (runs AFTER base checks). A string names the missing item for the Save tooltip.
 * @param {function} [customValidation] - (form) => boolean to REPLACE base validation entirely (for providers like MaaS)
 * @param {object} [availabilityOptions] - extra options for useInstanceAvailability (e.g. zoneId override, `enabled: false` for providers with their own live catalog)
 * @param {string} [zoneField] - form field name to use as zoneId (defaults to 'zoneId')
 * @param {boolean} [requiresOs=true] - whether an Operating System must be chosen before saving.
 *        Set false only for providers that hide the OS picker and derive it from the image (IBM).
 * @param {function} [onInit] - extra dispatches to run on mount (e.g. getAcmImages for MaaS)
 */
export default function useClusterForm({ provider, props, formHelpers, extraValidation, customValidation, availabilityOptions = {}, zoneField, onInit, requiresOs = true }) {
    const dispatch = useDispatch();
    const classes = useStyles(props);

    // Redux state
    const clusterSettings = useSelector(({ settings }) => settings.settings.clusterSettings);
    const orgClusterSettings = useSelector(({ settings }) => settings.settings.orgClusterSettings);
    const cluster = useSelector(({ clusters }) => clusters.cluster.cluster);
    const loading = useSelector(({ fuse }) => fuse.settings.loading);
    const { volSizeLimits, instanceSizes } = useProviderData(provider);

    // Form state
    const [form, setForm] = useState(props.oldForm || null);
    const setField = useCallback((keyOrChanges, value) => {
        setForm(prev => ({
            ...prev,
            ...(typeof keyOrChanges === 'string' ? { [keyOrChanges]: value } : keyOrChanges),
        }));
    }, []);

    // Marketplace
    const marketplace = useMarketplace({ provider, form, setField, propsMarketId: props.marketId });

    // UI state
    const [advanced, setAdvanced] = useState(false);
    const { selectedOwner, setSelectedOwner } = useClusterOwner(form, props.type);
    const disableInputs = props.type === 'edit' && Boolean(form?.state) && form.state !== CLUSTER_STATES.DESTROYED;

    // Validation — customValidation fully replaces base checks, extraValidation extends them.
    // Returns BOTH a boolean and a human-readable reason so the Save/Update button can
    // explain *why* it is disabled via a tooltip instead of sitting greyed-out with no
    // feedback — the #1 source of "why won't it let me update?" confusion.
    const { isFormValid, formInvalidReason } = useMemo(() => {
        const ok = { isFormValid: true, formInvalidReason: '' };
        const fail = (reason) => ({ isFormValid: false, formInvalidReason: reason });

        if (!form) return fail('Loading cluster…');
        if (customValidation) {
            return customValidation(form) ? ok : fail('Complete all required fields.');
        }

        const missing = [];
        if (!form.displayName) missing.push('cluster name');
        // The API derives server.ssh_username from the chosen OS, and Mongoose
        // rejects the create without it ("missing JSON key(s): ssh_username").
        // The OS <TextField> is marked `required`, but that is only the asterisk -
        // MUI does not block submit - so nothing stopped a save with no OS and the
        // POST came back 400. Providers that hide the OS picker (IBM derives it
        // from the selected image) opt out with requiresOs: false.
        if (requiresOs && !form.osType) missing.push('operating system');
        if (!form.credentialId) missing.push('credential');
        if (!form.regionId) missing.push('region');
        if (!form.serverInstanceId) missing.push('head node size');
        const imageType = form.serverImageType || IMAGE_TYPE.HEIDI_STACK;
        if (imageType === IMAGE_TYPE.HEIDI_STACK && !form.serverImageName) missing.push('image');
        // A node group only needs an instance type when it will actually provision nodes.
        // A head-only group (count 0) keeps an empty instance_id by design, so requiring
        // one here would dead-lock the Save/Update button for every head-only cluster.
        const groupProvisionsNodes = (g) => (g.count == null ? 1 : Number(g.count)) > 0;
        if ((form.nodeGroups || []).some(g => groupProvisionsNodes(g) && !g.instance_id)) {
            missing.push('a compute node instance type');
        }
        // Moab hostname + IP are required whenever manager === 'torque'. Torque
        // clusters on this platform are Moab-managed, and a Torque head deployed
        // without Moab info fails at deploy time (manage-hosts.sh "Missing
        // arguments"). IBM was previously excluded here, which let an IBM Torque
        // cluster save/update with empty Moab and then fail on deploy.
        if (form.manager === 'torque') {
            if (!form.moabHostname) missing.push('Moab hostname');
            if (!form.moabIp || !ipValid(form.moabIp)) missing.push('a valid Moab IP');
        }
        if (extraValidation) {
            // Providers return `true` when satisfied. Anything else fails; a string is
            // taken as the name of what is missing, so the disabled-Save tooltip can say
            // "a valid Server Volume size" instead of the useless generic fallback.
            const extra = extraValidation(form);
            if (extra !== true) {
                missing.push(typeof extra === 'string' ? extra : 'a required field for this provider');
            }
        }
        return missing.length === 0 ? ok : fail(`Complete the following before saving: ${missing.join(', ')}.`);
    }, [form, extraValidation, customValidation, provider, requiresOs]);

    // Instance availability
    const computeNodeInstanceTypes = useMemo(
        () => (form?.nodeGroups || []).map(g => g.instance_id).filter(Boolean),
        [form?.nodeGroups]
    );
    const availability = useInstanceAvailability({
        provider,
        credentialId: form?.credentialId,
        region: form?.regionId,
        zoneId: availabilityOptions.zoneId !== undefined ? availabilityOptions.zoneId : form?.[zoneField || 'zoneId'],
        headNodeInstance: form?.serverInstanceId,
        computeNodeInstances: computeNodeInstanceTypes,
        enabled: availabilityOptions.enabled !== false && Boolean(form?.credentialId && form?.regionId),
    });

    // Quotas
    const quotas = useQuotas(provider, form);

    // === EFFECTS ===

    useEffect(() => {
        dispatch(StackActions.getStacks());
        dispatch(getClusterSettings());
        dispatch(getOrgClusterSettings());
        if (onInit) onInit(dispatch);
    }, [dispatch]);

    // Initialize form once
    const formInitialized = useRef(false);
    useEffect(() => {
        if (formInitialized.current) return;
        if (!clusterSettings) return;
        // Also wait for org-scoped cluster settings before initializing.
        // Several initNewForm defaults (capacity-reservation, default
        // remote protocol, the heidi-pip preset opt-out) read
        // orgClusterSettings.clusterSettings.* and the dispatch on form
        // mount races with the cluster-wide settings load. If we
        // initialize while orgClusterSettings is still null, those
        // org-level defaults silently fall back to "off" and never get
        // re-applied (formInitialized.current is true after first run,
        // so the effect's later re-fires bail).
        if (props.type === 'new' && !orgClusterSettings) return;

        if (props.type === 'new') {
            formInitialized.current = true;
            setForm(formHelpers.initNewForm(provider, {
                marketplaceConfig: marketplace.config,
                clusterSettings,
                orgClusterSettings,
                marketId: marketplace.marketId,
                marketArch: marketplace.selectedArch,
                marketOs: marketplace.selectedOs,
            }));
        } else if (props.type === 'edit' && cluster) {
            formInitialized.current = true;
            setForm(formHelpers.initEditForm(cluster, { clusterSettings, orgClusterSettings }));
        }
    }, [props.type, cluster, clusterSettings, orgClusterSettings, marketplace.config, marketplace.marketId, marketplace.selectedArch, marketplace.selectedOs]);

    // === HANDLERS ===

    const handleClose = useCallback(() => {
        dispatch(ActionsDialog.closeDialog());
    }, [dispatch]);

    // Deliberately does NOT call handleClose(). `saveCluster` already closes the
    // dialog itself on success, and on failure it shows an error snackbar and
    // leaves the dialog open so the user keeps what they typed. Closing here
    // fired synchronously, before the request resolved, so a rejected create
    // (e.g. 400 "missing JSON key(s): ssh_username") looked exactly like a
    // successful one: dialog gone, no error, no cluster. Matches handleEdit,
    // which has always left closing to its action.
    const handleSubmit = useCallback(() => {
        dispatch(Actions.saveCluster(formHelpers.toApiShape(form)));
    }, [dispatch, form]);

    const handleEdit = useCallback(() => {
        dispatch(Actions.editCluster(formHelpers.toApiShape(form)));
    }, [dispatch, form]);

    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape') handleClose(); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleClose]);

    return {
        form, setForm, setField,
        advanced, setAdvanced,
        classes, loading,
        marketplace,
        selectedOwner, setSelectedOwner,
        disableInputs,
        isFormValid, formInvalidReason,
        volSizeLimits, instanceSizes,
        computeNodeInstanceTypes,
        availability, quotas,
        handleClose, handleSubmit, handleEdit,
        dispatch,
    };
}
